import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useAudit } from "./AuditContext";
import { tapFeedback } from "./haptics";
import { PresenterFloorShadow } from "./PresenterFloorShadow";
import { useIsMobileAudit } from "./useIsMobileAudit";

/**
 * Rehearsal Room · Room 02 — STRUCTURE
 * ====================================
 * Shares the exact scene chrome as Room 01 (same bg_message.png, same
 * presenter pose on the left, same corner mark, same speech-bubble
 * convention). The whiteboard content is different:
 *
 *   TOP    — Structure cards the user can drag onto the timeline.
 *   BOTTOM — Horizontal "BUILD YOUR FLOW" timeline with 7 slots
 *            connected by simple hand-drawn arrows, framed by
 *            START and END markers.
 *
 * Interaction: HTML5 drag-and-drop between the available pool and
 * timeline slots. Cards can be moved between slots and dropped back
 * into the available pool by dragging out.
 *
 * No floating overlays, no modal cards — the interaction lives ON the
 * board so it stays consistent with Room 01's rehearsal-room feel.
 */

// ---------------------------------------------------------------------------
// Content
// ---------------------------------------------------------------------------
const STRUCTURE_CARDS = [
  { id: "context",        label: "Context" },
  { id: "problem",        label: "Problem" },
  { id: "story",          label: "Story" },
  { id: "evidence",       label: "Evidence" },
  { id: "solution",       label: "Solution" },
  { id: "recommendation", label: "Recommendation" },
  { id: "callToAction",   label: "Call to Action" },
];

const INTRO_STEPS = [
  "Now you have the ingredients.",
  "But a presentation also needs a path.",
  "How would you naturally organise the story?",
  "Build the flow that feels right to you.",
];

const SUCCESS_STEPS = [
  "Good. That tells me something about how you organise ideas.",
  "Now let\u2019s see what happens when those ideas are spoken.",
  "Let\u2019s go to Voice.",
];

const REFLECTION_QUESTION = "What guided the order you created most?";
const REFLECTION_OPTIONS = [
  { id: "clarity",          label: "Clarity",           hint: "Making the story easy to follow" },
  { id: "logic",            label: "Logic",             hint: "Putting each idea where it made most sense" },
  { id: "persuasion",       label: "Persuasion",        hint: "Building toward the conclusion or action" },
  { id: "audienceInterest", label: "Audience interest", hint: "Keeping people engaged as the story develops" },
  { id: "instinct",         label: "Instinct",          hint: "It simply felt like the natural order" },
];

const TIMELINE_SLOTS = 7;
const MIN_FLOW = 3; // minimum cards to consider the flow "meaningful"

const CARD_ROTATIONS = [-1.5, 1.1, -0.8, 1.6, -1.9, 0.7, -1.2];

// Same soft mask as Room 01 (hides sprite artifacts).
const PRESENTER_MASK =
  "linear-gradient(to bottom, transparent 0%, black 5%, black 95%, transparent 100%)";

// ---------------------------------------------------------------------------
// Structure card (physical rehearsal card)
// ---------------------------------------------------------------------------
const Pin = () => (
  <span
    aria-hidden="true"
    className="absolute -top-1.5 left-1/2 -translate-x-1/2"
    style={{ width: 10, height: 10, zIndex: 2 }}
  >
    <span
      className="block w-full h-full rounded-full"
      style={{
        background: "radial-gradient(circle at 35% 30%, #ff5b4c 0%, #c62b1a 55%, #7a1206 100%)",
        boxShadow: "0 1.5px 2px rgba(0,0,0,0.45), inset 0 -1px 1px rgba(0,0,0,0.35)",
      }}
    />
  </span>
);

function StructureCard({ card, index, interactive, placed = false, onDragStart, onTap }) {
  const rotation = placed ? 0 : CARD_ROTATIONS[index % CARD_ROTATIONS.length];
  return (
    <button
      type="button"
      draggable={interactive}
      onDragStart={(e) => {
        if (!interactive) return;
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", card.id);
        onDragStart?.(card.id);
      }}
      onClick={() => interactive && onTap?.(card.id)}
      disabled={!interactive}
      data-testid={`struct-card-${card.id}`}
      className={[
        "relative select-none w-[135px] min-h-[56px] px-3.5 py-2.5",
        "transition-transform duration-200",
        interactive ? "cursor-grab active:cursor-grabbing hover:-translate-y-0.5" : "cursor-default",
      ].join(" ")}
      style={{
        background: placed
          ? "linear-gradient(180deg, #FFFDF6 0%, #F4E9C6 100%)"
          : "linear-gradient(180deg, #FFF5CE 0%, #F0DE8E 100%)",
        boxShadow: placed
          ? "0 4px 10px rgba(60,40,10,0.28), inset 0 -2px 0 rgba(0,0,0,0.06)"
          : "0 6px 14px rgba(60,40,10,0.28), inset 0 -3px 0 rgba(0,0,0,0.06)",
        transform: `rotate(${rotation}deg)`,
        zIndex: 20,
      }}
    >
      {!placed && <Pin />}
      <p className="font-serif text-[14px] leading-tight text-ink text-center">
        {card.label}
      </p>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Arrow — hand-drawn look between timeline slots
// ---------------------------------------------------------------------------
const Arrow = () => (
  <span
    aria-hidden="true"
    className="font-serif text-ink/50 select-none"
    style={{ fontSize: 22, lineHeight: 1 }}
  >
    →
  </span>
);

// ---------------------------------------------------------------------------
// Timeline slot — accepts a dropped card
// ---------------------------------------------------------------------------
function Slot({ index, card, interactive, onDrop, onTap }) {
  return (
    <div
      onDragOver={(e) => interactive && e.preventDefault()}
      onDrop={(e) => {
        if (!interactive) return;
        e.preventDefault();
        const cid = e.dataTransfer.getData("text/plain");
        if (cid) onDrop(cid, index);
      }}
      onClick={() => interactive && card && onTap?.(card.id)}
      data-testid={`slot-${index + 1}`}
      className="relative flex flex-col items-center justify-center"
      style={{
        width: 142,
        height: 82,
        border: card ? "none" : "1.5px dashed rgba(60,45,25,0.45)",
        background: card ? "transparent" : "rgba(255,255,255,0.35)",
      }}
    >
      {card ? (
        <StructureCard
          card={card}
          index={index}
          interactive={interactive}
          placed
          onTap={onTap}
        />
      ) : (
        <span className="font-mono uppercase tracking-[0.24em] text-ink/45 text-[10px]">
          {index + 1}
        </span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Room component
// ---------------------------------------------------------------------------
export function RehearsalRoomStructure() {
  const {
    answers,
    setRoomAnswer,
    setTagsForRoom,
    setCanProceed,
    setOverrideMessage,
    setBeforeNext,
    setReflection,
    selectedPresenter,
    overrideMessage,
  } = useAudit();
  const isMobile = useIsMobileAudit();

  // Local step counters — mirror Room 01's pattern.
  const introStep     = answers.structure?.introStep     ?? 0;
  const successStep   = answers.structure?.successStep   ?? 0;
  const sequence      = answers.structure?.sequence      ?? Array(TIMELINE_SLOTS).fill(null);
  const flowConfirmed = answers.structure?.flowConfirmed ?? false;
  const reflectionPick = answers.structure?.reflection?.selected ?? null;
  const introDone     = introStep >= INTRO_STEPS.length;
  const placedCount   = sequence.filter(Boolean).length;
  const validFlow     = placedCount >= MIN_FLOW;
  const successDone   = flowConfirmed && !!reflectionPick && successStep >= SUCCESS_STEPS.length;
  const inReflectPick = flowConfirmed && !reflectionPick;
  // Success beats play only AFTER the user explicitly confirms with Continue.
  const beatsPlaying  = flowConfirmed && !!reflectionPick && !successDone;
  // The catcher is for the intro (before cards) and for the success beats
  // (after the user confirmed the flow) — NOT while the user is still placing.
  const inCatcher     = !introDone || beatsPlaying;

  // Card lookup helpers
  const cardById = (id) => STRUCTURE_CARDS.find((c) => c.id === id);
  const unusedIds = STRUCTURE_CARDS.map((c) => c.id).filter((id) => !sequence.includes(id));

  // Sync bubble + continue gate with the current sub-phase.
  useEffect(() => {
    if (!introDone) {
      setOverrideMessage(INTRO_STEPS[introStep]);
      setCanProceed(false);
      return;
    }
    // The user is still building the flow — no bubble, Continue enables
    // once at least MIN_FLOW cards are placed (up to 7 allowed).
    if (!flowConfirmed) {
      setOverrideMessage("");
      setCanProceed(validFlow);
      return;
    }
    // Reflection choice — locked until the user picks one card.
    if (!reflectionPick) {
      setOverrideMessage(REFLECTION_QUESTION);
      setCanProceed(false);
      return;
    }
    // Success beats are playing — Continue is locked until they finish.
    if (!successDone) {
      setOverrideMessage(SUCCESS_STEPS[successStep]);
      setCanProceed(false);
      return;
    }
    // All beats seen — Continue enabled to advance to Room 03.
    setOverrideMessage(SUCCESS_STEPS[SUCCESS_STEPS.length - 1]);
    setCanProceed(true);
  }, [introStep, introDone, validFlow, flowConfirmed, reflectionPick, successStep, successDone, setCanProceed, setOverrideMessage]);

  // Auto-confirm the flow the moment the user places all 7 cards — no need
  // to press Continue to hear the Presenter's completion dialogue.
  useEffect(() => {
    if (introDone && !flowConfirmed && placedCount === TIMELINE_SLOTS) {
      setRoomAnswer("structure", { flowConfirmed: true, successStep: 0 });
    }
  }, [introDone, flowConfirmed, placedCount, setRoomAnswer]);

  // Intercept the Continue click: the first click during "building" phase
  // must confirm the flow (start success beats), NOT advance to Room 03.
  useEffect(() => {
    setBeforeNext(() => {
      if (introDone && !flowConfirmed && validFlow) {
        // First Continue press → lock the flow and start the presenter beats.
        setRoomAnswer("structure", { flowConfirmed: true, successStep: 0 });
        return false; // don't advance yet
      }
      // If beats are playing, they must finish before advancing.
      if (flowConfirmed && !successDone) return false;
      return true;
    });
    return () => setBeforeNext(null);
  }, [introDone, flowConfirmed, validFlow, successDone, setBeforeNext, setRoomAnswer]);

  // Persist unused list + tags whenever sequence changes.
  useEffect(() => {
    setRoomAnswer("structure", { unused: unusedIds });
    const tags = sequence
      .filter(Boolean)
      .map((cid, i) => [`struct:${cid}`, `struct:pos:${i + 1}:${cid}`])
      .flat();
    setTagsForRoom("struct", tags);
  }, [sequence.join("|")]);

  const advance = () => {
    if (!introDone) {
      setRoomAnswer("structure", { introStep: introStep + 1 });
      return;
    }
    if (beatsPlaying) {
      setRoomAnswer("structure", { successStep: successStep + 1 });
    }
  };

  const pickReflection = (optionId) => {
    if (!inReflectPick) return;
    tapFeedback();
    const tag = `structure_reflection:${optionId}`;
    setRoomAnswer("structure", { reflection: { selected: optionId, tag }, successStep: 0 });
    setReflection("structure", { question: REFLECTION_QUESTION, selected: optionId, tag });
    setTagsForRoom("structure_reflection", [tag]);
  };

  // Interaction is disabled during intro AND once the user has confirmed
  // the flow (so the sequence stays fixed while the Presenter speaks).
  const interactive = introDone && !flowConfirmed;

  // Drag-drop
  const placeIntoSlot = (cardId, slotIdx) => {
    tapFeedback();
    const currentIdx = sequence.indexOf(cardId);
    let next = [...sequence];
    if (currentIdx !== -1) next[currentIdx] = null;              // remove from previous slot
    const displaced = next[slotIdx];
    next[slotIdx] = cardId;
    if (displaced && currentIdx !== -1) next[currentIdx] = displaced; // swap
    // Advance intro on first placement.
    const patch = { sequence: next };
    if (!introDone) patch.introStep = INTRO_STEPS.length;
    setRoomAnswer("structure", patch);
  };

  const removeFromTimeline = (cardId) => {
    const idx = sequence.indexOf(cardId);
    if (idx === -1) return;
    const next = [...sequence];
    next[idx] = null;
    setRoomAnswer("structure", { sequence: next });
  };

  // Tap-to-place — pick the first empty slot; if none, put into last.
  const tap = (cardId) => {
    const alreadyIdx = sequence.indexOf(cardId);
    if (alreadyIdx !== -1) return removeFromTimeline(cardId);
    const emptyIdx = sequence.findIndex((s) => s === null);
    const target = emptyIdx === -1 ? sequence.length - 1 : emptyIdx;
    placeIntoSlot(cardId, target);
  };

  const presenter = selectedPresenter;
  // While the user is placing cards on the timeline (task not yet confirmed),
  // shrink the presenter into a listening pose — same feel as Rooms 03 & 04.
  const isListening = introDone && !flowConfirmed;
  const pose = flowConfirmed && successStep >= SUCCESS_STEPS.length - 1
    ? "walking"
    : (isListening ? "listening" : "presentingAlt");
  const poseFile = presenter?.poses?.[pose] || presenter?.poses?.neutral;
  const presenterSrc = presenter ? `${presenter.avatarBase}/${poseFile}` : "";

  const totalCatcherSteps = !introDone ? INTRO_STEPS.length : SUCCESS_STEPS.length;
  const currentCatcherStep = !introDone ? introStep + 1 : successStep + 1;

  return (
    <div
      className="absolute inset-0 overflow-hidden"
      data-testid="rehearsal-room-structure"
      style={{ background: "#141310" }}
    >
      {/* Background — same whiteboard scene as Room 01 */}
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          backgroundImage: "url(/audit/bg_message.webp)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />

      {/* Presenter — same left/height math as Room 01's "presenting" close-up
          (same whiteboard background, same 1400px asset set: ~6.2% blank
          canvas above/below the figure, compensated via `bottom`). Shrinks
          during the card-placement task to a normal, fully-grounded figure
          off to the side. */}
      {presenter && (
        <motion.div
          className="absolute overflow-hidden"
          animate={{
            // "height" was originally fixed px (806.4 / 1152), tuned against
            // a ~900px-tall desktop viewport. Expressed as the SAME ratio
            // (÷900*100) it now scales correctly on any viewport height —
            // see RehearsalRoom.jsx's identical "106.7%" fix for the
            // full calibration note.
            left: isListening ? "-10%" : (pose === "walking" ? "calc(-25% + 225px)" : "calc(-25% + 65px)"),
            bottom: isListening ? "-3.75%" : (pose === "walking" ? "-35.9%" : "-57.3%"),
            height: isListening ? "60.5%" : (pose === "walking" ? "89.6%" : "128%"),
          }}
          initial={false}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          style={{ zIndex: 10 }}
          data-testid="structure-presenter"
        >
          <PresenterFloorShadow bottom="6.2%" />
          <AnimatePresence initial={false} mode="popLayout">
            <motion.img
              key={pose}
              src={presenterSrc}
              alt={presenter.name}
              draggable={false}
              initial={{ opacity: 0, y: 4, scaleX: (pose === "listening" || pose === "walking") ? -1 : 1 }}
              animate={{ opacity: 1, y: 0, scaleX: (pose === "listening" || pose === "walking") ? -1 : 1 }}
              exit={{ opacity: 0, y: -4, scaleX: (pose === "listening" || pose === "walking") ? -1 : 1 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="h-full w-auto select-none block"
              style={{
                mixBlendMode: "multiply",
                filter: "brightness(0.95) sepia(0.1)",
                WebkitMaskImage: PRESENTER_MASK,
                maskImage: PRESENTER_MASK,
              }}
              data-testid={`structure-presenter-img-${pose}`}
            />
          </AnimatePresence>
        </motion.div>
      )}

      {/* Speech bubble — same styling and position as Room 01 interact.
          Hidden while the user is placing timeline cards (isListening) so
          the task has focus, matching Rooms 03 & 04. */}
      <AnimatePresence initial={false}>
        {overrideMessage && !isListening ? (
          <motion.div
            key={overrideMessage}
            initial={{ opacity: 0, y: 6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.98 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="absolute"
            style={{ top: 45, left: 150, zIndex: 100, maxWidth: 420 }}
            data-testid="structure-bubble"
          >
            <div
              className="relative bg-white text-[#1B1B18] px-7 py-5 shadow-[0_10px_30px_rgba(0,0,0,0.35)]"
              style={{ borderRadius: 30, transform: "translateY(0px)" }}
            >
              <p className="font-serif text-xl leading-snug">{overrideMessage}</p>
              <span
                aria-hidden="true"
                className="absolute"
                style={{
                  left: 28,
                  bottom: -16,
                  width: 0,
                  height: 0,
                  borderLeft: "14px solid transparent",
                  borderRight: "8px solid transparent",
                  borderTop: "20px solid #ffffff",
                  filter: "drop-shadow(0 4px 4px rgba(0,0,0,0.15))",
                }}
              />
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>

      {/* Corner mark — top-right */}
      <div
        className="absolute right-6 top-5 flex flex-col items-end pointer-events-none"
        style={{ zIndex: 50 }}
        data-testid="structure-cornermark"
      >
        <p className="font-serif italic text-ink text-lg md:text-2xl leading-none tracking-wide drop-shadow-[0_2px_6px_rgba(255,240,210,0.4)]">
          InterAct Globally
        </p>
        <p className="font-hand text-terracotta/90 text-base md:text-xl leading-tight mt-1 drop-shadow-[0_2px_6px_rgba(255,240,210,0.4)]">
          the rehearsal room
        </p>
      </div>

      {/* Whiteboard content — cleared during the reflection phase. On mobile,
          stays hidden until the intro beats finish (Presenter-only opening). */}
      <AnimatePresence initial={false}>
      {!flowConfirmed && (!isMobile || introDone) && (
      <motion.div
        exit={{ opacity: 0, transition: { duration: 0.45 } }}
        className="relative w-full h-full"
        style={{
          marginLeft: "20%",
          paddingTop: "calc(18% - 10px)",
          paddingRight: "6%",
          zIndex: 15,
        }}
        data-testid="structure-board"
      >
        {/* Progress helper — sits ABOVE the available cards */}
        {introDone && !flowConfirmed && (
          <p
            className={`font-mono uppercase tracking-[0.22em] text-[13px] mb-2 text-center ${
              validFlow ? "text-terracotta" : "text-ink/60"
            }`}
            style={{
              marginTop: "-80px",
              marginLeft: "calc(2% - 18px)",
              marginRight: "calc(8% + 33px)",
            }}
            data-testid="flow-progress"
          >
            {placedCount} / {TIMELINE_SLOTS} placed
            {validFlow && " · press continue when you\u2019re happy with your flow"}
          </p>
        )}

        {/* AVAILABLE CARDS — upper part of the whiteboard */}
        <div
          onDragOver={(e) => interactive && e.preventDefault()}
          onDrop={(e) => {
            if (!interactive) return;
            e.preventDefault();
            const cid = e.dataTransfer.getData("text/plain");
            if (cid) removeFromTimeline(cid);
          }}
          className="flex flex-wrap gap-3 items-start justify-center"
          style={{
            marginTop: "25px",
            marginLeft: "calc(2% - 18px)",
            marginRight: "calc(8% + 33px)",
          }}
          data-testid="structure-available"
        >
          {unusedIds.map((id, i) => (
            <StructureCard
              key={id}
              card={cardById(id)}
              index={i}
              interactive={interactive}
              onTap={tap}
            />
          ))}
        </div>

        {/* BUILD YOUR FLOW — lower part */}
        <div
          className="absolute left-0 right-0 flex flex-col items-center"
          style={{
            bottom: "calc(20% + 10px)",
            marginLeft: "calc(2% - 45px)",
            marginRight: "calc(8% + 60px)",
          }}
          data-testid="structure-flow"
        >
          <p
            className="font-hand text-ink/80 mb-3"
            style={{ fontSize: 26, lineHeight: 1 }}
          >
            Build your flow
          </p>

          <div className="flex flex-col items-center gap-3">
            {/* Row 1 — Start · [1] [2] [3] [4] · wrap → */}
            <div className="flex flex-row items-center gap-2 flex-nowrap">
              <span
                className="font-mono uppercase tracking-[0.24em] text-ink/70 px-1"
                style={{ fontSize: 10 }}
                data-testid="flow-start"
              >
                Start
              </span>
              <Arrow />
              {sequence.slice(0, 4).map((cardId, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <Slot
                    index={idx}
                    card={cardId ? cardById(cardId) : null}
                    interactive={interactive}
                    onDrop={placeIntoSlot}
                    onTap={tap}
                  />
                  {idx < 3 && <Arrow />}
                </div>
              ))}
              {/* wrap-to-next-row arrow */}
              <span
                aria-hidden="true"
                className="font-serif text-ink/50 select-none"
                style={{ fontSize: 22, lineHeight: 1 }}
              >
                ↴
              </span>
            </div>

            {/* Row 2 — wrap-continues → [5] [6] [7] · End */}
            <div className="flex flex-row items-center gap-2 flex-nowrap">
              <span
                aria-hidden="true"
                className="font-serif text-ink/50 select-none"
                style={{ fontSize: 22, lineHeight: 1 }}
              >
                ↳
              </span>
              {sequence.slice(4, TIMELINE_SLOTS).map((cardId, i) => {
                const idx = i + 4;
                return (
                  <div key={idx} className="flex items-center gap-2">
                    <Slot
                      index={idx}
                      card={cardId ? cardById(cardId) : null}
                      interactive={interactive}
                      onDrop={placeIntoSlot}
                      onTap={tap}
                    />
                    {idx < TIMELINE_SLOTS - 1 && <Arrow />}
                  </div>
                );
              })}
              <Arrow />
              <span
                className="font-mono uppercase tracking-[0.24em] text-ink/70 px-1"
                style={{ fontSize: 10 }}
                data-testid="flow-end"
              >
                End
              </span>
            </div>
          </div>

          {/* Progress helper moved above the available cards */}
        </div>
      </motion.div>
      )}
      </AnimatePresence>

      {/* Reflection cards — after the flow is confirmed, before the closing beats */}
      <AnimatePresence>
        {inReflectPick && (
          <motion.div
            key="structure-reflection-cards"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="absolute grid grid-cols-2 gap-4"
            style={{ top: "28%", left: "28%", right: "7%", zIndex: 60 }}
            data-testid="structure-reflection-cards"
          >
            {REFLECTION_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => pickReflection(opt.id)}
                data-testid={`structure-reflection-${opt.id}`}
                className="text-left px-6 py-4 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_14px_36px_rgba(0,0,0,0.25)]"
                style={{
                  background: "rgba(242, 235, 218, 0.92)",
                  border: "1px solid rgba(90,60,25,0.16)",
                  boxShadow: "0 6px 18px rgba(0,0,0,0.16)",
                  ...(opt.id === "instinct" ? { gridColumnStart: 2 } : {}),
                }}
              >
                <p className="font-serif text-xl md:text-2xl leading-snug text-ink">{opt.label}</p>
                <p className="mt-1 font-body text-sm text-ink/70">&ldquo;{opt.hint}&rdquo;</p>
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Intro/success advancer — same catcher pattern as Room 01 */}
      {inCatcher && (
        <button
          type="button"
          onClick={advance}
          data-testid="structure-advance"
          aria-label="Continue"
          className="absolute inset-0 cursor-pointer bg-transparent pointer-events-auto"
          style={{ zIndex: 88 }}
        >
          <span
            aria-hidden="true"
            className="absolute bottom-24 left-1/2 -translate-x-1/2 font-mono text-[10px] uppercase tracking-[0.28em] text-ink/60 animate-pulse"
          >
            tap to continue &nbsp;·&nbsp; {currentCatcherStep} / {totalCatcherSteps}
          </span>
        </button>
      )}
    </div>
  );
}

export default RehearsalRoomStructure;
